import {
    DestroyRef,
    Directive,
    ElementRef,
    afterNextRender,
    inject,
} from '@angular/core';

/**
 * Horizontal table wrappers (`overflow-x: auto` + `overflow-y: clip`) become scrollports that
 * swallow vertical wheel/touch even though they cannot scroll vertically — especially when the
 * table is taller than the wrapper (scrollHeight > clientHeight) and `overscroll-behavior-y`
 * blocks chaining. Forward vertical gestures to the nearest real vertical scroller (dialog in
 * Add mode, preview pane in View mode).
 */
@Directive({
    selector: '.add-tables-scroll-x-only, .add-tables-preview-scroll-x',
    standalone: true,
})
export class AddTablesScrollChainDirective {
    private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
    private readonly destroyRef = inject(DestroyRef);

    private lastY = 0;
    private lastX = 0;
    private tracking = false;

    constructor() {
        afterNextRender(() => {
            this.bindScrollChain();
        });
    }

    private bindScrollChain(): void {
        if (typeof window === 'undefined') {
            return;
        }

        const el = this.host.nativeElement;
        const isNarrow = window.matchMedia('(max-width: 1280px)').matches;

        const canScrollVertically = (node: HTMLElement): boolean => {
            const style = getComputedStyle(node);
            const overflowY = style.overflowY;
            if (overflowY !== 'auto' && overflowY !== 'scroll') {
                return false;
            }
            return node.scrollHeight > node.clientHeight + 1;
        };

        const findVerticalScroller = (
            node: HTMLElement | null,
        ): HTMLElement | null => {
            while (node) {
                if (canScrollVertically(node)) {
                    return node;
                }
                node = node.parentElement;
            }
            return null;
        };

        const forwardVertical = (deltaY: number, event: Event): void => {
            if (deltaY === 0 || canScrollVertically(el)) {
                return;
            }

            const parentScroller = findVerticalScroller(el.parentElement);
            if (!parentScroller) {
                return;
            }

            parentScroller.scrollTop += deltaY;
            event.preventDefault();
        };

        const onWheel = (e: WheelEvent): void => {
            // Keep shift-wheel / trackpad horizontal pans on the horizontal scroller.
            if (e.shiftKey || Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
                return;
            }
            forwardVertical(e.deltaY, e);
        };

        const onTouchStart = (e: TouchEvent): void => {
            if (e.touches.length !== 1) {
                return;
            }
            this.lastY = e.touches[0].clientY;
            this.lastX = e.touches[0].clientX;
            this.tracking = true;
        };

        const onTouchMove = (e: TouchEvent): void => {
            if (!this.tracking || e.touches.length !== 1) {
                return;
            }

            if (canScrollVertically(el)) {
                return;
            }

            const y = e.touches[0].clientY;
            const x = e.touches[0].clientX;
            const dy = y - this.lastY;
            const dx = x - this.lastX;

            this.lastY = y;
            this.lastX = x;

            if (Math.abs(dy) <= Math.abs(dx)) {
                return;
            }

            // Touch dy is opposite wheel deltaY convention for scrollTop.
            forwardVertical(-dy, e);
        };

        const onTouchEnd = (): void => {
            this.tracking = false;
        };

        el.addEventListener('wheel', onWheel, { passive: false });

        // Touch forwarding is only needed on stacked (narrow) layouts.
        if (isNarrow) {
            el.addEventListener('touchstart', onTouchStart, { passive: true });
            el.addEventListener('touchmove', onTouchMove, { passive: false });
            el.addEventListener('touchend', onTouchEnd, { passive: true });
            el.addEventListener('touchcancel', onTouchEnd, { passive: true });
        }

        this.destroyRef.onDestroy(() => {
            el.removeEventListener('wheel', onWheel);
            if (isNarrow) {
                el.removeEventListener('touchstart', onTouchStart);
                el.removeEventListener('touchmove', onTouchMove);
                el.removeEventListener('touchend', onTouchEnd);
                el.removeEventListener('touchcancel', onTouchEnd);
            }
        });
    }
}
